import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Save } from "lucide-react";

export default function OpsCheck() {
  const [types, setTypes] = useState([]);
  const [pddTypeNames, setPddTypeNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([
      api.get("/settings/ops-check").then((r) => setTypes(r.data.loan_types || [])).catch(() => {}),
      api.get("/settings/pdd").then((r) => setPddTypeNames((r.data.loan_types || []).map((t) => t.name))).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);
  const addType = () => setTypes((t) => [...t, { name: "", activities: [{ name: "", requires_document: false, deviation_allowed: false }] }]);
  const delType = (i) => setTypes((t) => t.filter((_, ix) => ix !== i));
  const updType = (i, patch) => setTypes((t) => t.map((x, ix) => ix === i ? { ...x, ...patch } : x));
  const addAct = (i) => updType(i, { activities: [...(types[i].activities || []), { name: "", requires_document: false, deviation_allowed: false }] });
  const delAct = (i, j) => updType(i, { activities: types[i].activities.filter((_, jx) => jx !== j) });
  const updAct = (i, j, patch) => updType(i, { activities: types[i].activities.map((a, jx) => jx === j ? { ...a, ...patch } : a) });
  const save = async () => {
    setSaving(true);
    try {
      const clean = types.filter((t) => (t.name || "").trim()).map((t) => ({
        name: t.name.trim(),
        activities: (t.activities || []).filter((a) => (a.name || "").trim()).map((a) => ({
          name: a.name.trim(), requires_document: !!a.requires_document, deviation_allowed: !!a.deviation_allowed,
        })),
      }));
      const { data } = await api.put("/settings/ops-check", { loan_types: clean });
      setTypes(data.loan_types || []);
      toast.success("Ops Check settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (loading) return <div className="py-24 text-center text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5 max-w-4xl" data-testid="ops-check-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2"><ShieldCheck size={24} className="text-emerald-600" /> Ops Check Setup</h1>
        <p className="text-sm text-slate-500 mt-1">Define the checklist the Operations Manager must verify (with remark + optional document upload) before approving a lead. Loan types should match the ones configured under PDD Checklist.</p>
        {pddTypeNames.length > 0 && (
          <p className="text-xs text-slate-400 mt-1">PDD loan types available: {pddTypeNames.join(", ")}</p>
        )}
      </div>
      <button data-testid="ops-check-add-loan-type" onClick={addType} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-sm inline-flex items-center gap-2"><Plus size={16} /> Add Loan Type</button>
      {types.length === 0 && <p className="text-sm text-slate-400">No loan types configured. Click "Add Loan Type" to begin.</p>}
      {types.map((t, i) => (
        <div key={i} data-testid={`ops-check-loan-type-${i}`} className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3">
          <div className="flex items-center gap-2">
            <input list={`pdd-lt-list-${i}`} data-testid={`ops-check-loan-type-name-${i}`} value={t.name} onChange={(e) => updType(i, { name: e.target.value })} className={`${inputCls} flex-1`} placeholder="Loan Type name (matches PDD)" />
            <datalist id={`pdd-lt-list-${i}`}>{pddTypeNames.map((n) => <option key={n} value={n} />)}</datalist>
            <button data-testid={`ops-check-del-loan-type-${i}`} onClick={() => delType(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={16} /></button>
          </div>
          <div className="grid grid-cols-12 gap-2 px-1 pt-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            <div className="col-span-7">Ops Check Item</div>
            <div className="col-span-4">Doc Upload Required</div>
            <div className="col-span-1" />
          </div>
          {(t.activities || []).map((a, j) => (
            <div key={j} className="grid grid-cols-12 gap-2 items-center" data-testid={`ops-check-item-${i}-${j}`}>
              <input value={a.name} onChange={(e) => updAct(i, j, { name: e.target.value })} className={`${inputCls} col-span-7`} placeholder="e.g. Verify Aadhaar+PAN match" />
              <label className="col-span-4 flex items-center gap-2 text-sm text-slate-700"><input data-testid={`ops-check-requires-doc-${i}-${j}`} type="checkbox" checked={!!a.requires_document} onChange={(e) => updAct(i, j, { requires_document: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Required</label>
              <button onClick={() => delAct(i, j)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 justify-self-end"><Trash2 size={15} /></button>
            </div>
          ))}
          <button data-testid={`ops-check-add-item-${i}`} onClick={() => addAct(i)} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium inline-flex items-center gap-1"><Plus size={13} /> Add Item</button>
        </div>
      ))}
      <div className="flex justify-end">
        <button data-testid="ops-check-save" onClick={save} disabled={saving} className={btnPrimary}><Save size={16} /> Save Settings</button>
      </div>
    </div>
  );
}
